import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import {
  FaFileExcel,
  FaPen,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";

import * as XLSX from "xlsx";

import { saveAs } from "file-saver";

import MainLayout from "../../components/Layouts/Main/MainLayout.index";

import {
  deleteProRecommendationCandidate,
  getProRecommendationCandidates,
  saveProRecommendationCandidate,
  searchProRecommendationUsers,
} from "../../api/project";

import {
  ActionCell,
  ActionGroup,
  Card,
  CardBody,
  CardHeader,
  ColorCell,
  ColorInputRow,
  ColorPicker,
  ColorSwatch,
  EmptyState,
  ErrorText,
  Field,
  FormGrid,
  FullWidthField,
  Hint,
  Input,
  Label,
  ModalButtons,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Muted,
  RecommendationBody,
  RecommendationPageLayout,
  Required,
  SearchInput,
  SearchRow,
  Select,
  SmallButton,
  Table,
  TableWrapper,
  TextArea,
  Title,
  ToolbarRow,
} from "./RecommendationCandidates.style";

const EMPTY_FORM = {
  id: "",

  user_id: "",

  user_name: "",

  show_user_name: "",

  hex_code: "#FFFFFFFF",

  hex_font_code: "#000000",

  memo: "",

  registration_type: 0,

  display_flag: 1,

  data_type: 0,
};

const CANDIDATE_OPTIONS = {
  registration_type: ["自動", "固定"],

  display_flag: ["非表示", "表示"],

  data_type: [
    "スケジュール新規ありまたは3日以内",

    "お気に入り登録指定数以上",

    "新規承認は7日以内",
  ],
};

const CANDIDATE_LABELS = {
  registration_type: "登録区分",

  display_flag: "表示フラグ",

  data_type: "データ区分",
};

const optionLabel = (field, value) =>
  CANDIDATE_OPTIONS[field][value] ?? displayValue(value);

const asText = (value) =>
  value === null || value === undefined ? "" : String(value);

const displayValue = (value) => {
  const text = asText(value).trim();

  return text || "-";
};

const formatDateTime = (value) => {
  const text = asText(value).trim();

  if (!text) return "-";

  const date = /^\d+$/.test(text)
    ? new Date(Number(text))
    : new Date(text.replace(" ", "T"));

  if (Number.isNaN(date.getTime())) return text;

  return date

    .toLocaleString("ja-JP", {
      year: "numeric",

      month: "2-digit",

      day: "2-digit",

      hour: "2-digit",

      minute: "2-digit",

      second: "2-digit",

      hour12: false,
    })

    .replace(/\//g, "-");
};

const isValidHex = (value) => {
  const text = asText(value).trim();

  return (
    !text ||
    /^#?(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text)
  );
};

const toColorInputValue = (value) => {
  const text = asText(value).trim();

  const match = text.match(
    /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i,
  );

  if (!match) return "#ffffff";

  let hex = match[1];

  // The native color picker accepts RGB only; retain alpha in the text field.

  if (hex.length === 4 || hex.length === 8) hex = hex.slice(0, -hex.length / 4);

  if (hex.length === 3) {
    hex = hex

      .split("")

      .map((character) => character + character)

      .join("");
  }

  return `#${hex}`;
};

const swatchColor = (value) => {
  const text = asText(value).trim();

  if (!text) return "#f5f5f5";

  const withHash = text.startsWith("#") ? text : `#${text}`;

  return isValidHex(text) ? withHash : "#eeeeee";
};

export default function RecommendationCandidates() {
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState([]);

  const [keyword, setKeyword] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    keyword: "",

    registration_type: "",

    display_flag: "",

    data_type: "",
  });

  const [draftFilters, setDraftFilters] = useState({
    registration_type: "",

    display_flag: "",

    data_type: "",
  });

  const [exporting, setExporting] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [modalOpen, setModalOpen] = useState(false);

  const [editingId, setEditingId] = useState("");

  const [form, setForm] = useState({ ...EMPTY_FORM });

  const [saving, setSaving] = useState(false);

  const [deletingId, setDeletingId] = useState("");

  const requestIdRef = useRef(0);

  const [userPickerOpen, setUserPickerOpen] = useState(false);

  const [userKeyword, setUserKeyword] = useState("");

  const [userQuery, setUserQuery] = useState("");

  const [users, setUsers] = useState([]);

  const [userPage, setUserPage] = useState(1);

  const [userHasMore, setUserHasMore] = useState(false);

  const [userLoading, setUserLoading] = useState(false);

  const [userError, setUserError] = useState("");

  const userRequestIdRef = useRef(0);

  const loadUsers = async (query = "", page = 1) => {
    const requestId = ++userRequestIdRef.current;

    setUserLoading(true);

    setUserError("");

    setUsers([]);

    setUserHasMore(false);

    setUserQuery(query);

    setUserPage(page);

    try {
      const response = await searchProRecommendationUsers(query, page);

      if (requestId !== userRequestIdRef.current) return;

      setUsers(Array.isArray(response?.data?.users) ? response.data.users : []);

      setUserHasMore(Boolean(response?.data?.hasMore));
    } catch (error) {
      if (requestId !== userRequestIdRef.current) return;

      setUserError(error.message || "ユーザーの取得に失敗しました");
    } finally {
      if (requestId === userRequestIdRef.current) setUserLoading(false);
    }
  };

  const closeUserPicker = useCallback(() => {
    ++userRequestIdRef.current;

    setUserPickerOpen(false);

    setUserLoading(false);
  }, []);

  useEffect(
    () => () => {
      ++userRequestIdRef.current;
    },

    [],
  );

  const openUserPicker = () => {
    setUserKeyword("");

    setUserPickerOpen(true);

    loadUsers();
  };

  const selectUser = (user) => {
    setForm((previous) => ({
      ...previous,

      user_id: asText(user.user_id),

      user_name: asText(user.user_name),

      show_user_name: asText(user.show_user_name),
    }));

    closeUserPicker();
  };

  const loadCandidates = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    setIsLoading(true);

    setErrorMessage("");

    try {
      // Fetch all records: search and categorical filters are client-side only.

      const response = await getProRecommendationCandidates();

      if (requestId !== requestIdRef.current) return;

      const data = response?.data;

      setCandidates(Array.isArray(data) ? data : []);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;

      console.error("Failed to load recommendation candidates:", error);

      setCandidates([]);

      const message = error?.message || "候補者一覧の取得に失敗しました";

      setErrorMessage(message);

      toast.error(message);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, []);

  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (field) => {
    if (sortColumn === field) {
      setSortDirection((previous) => (previous === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(field);
      setSortDirection("asc");
    }
  };

  const filteredCandidates = useMemo(() => {
    const query = appliedFilters.keyword.toLocaleLowerCase("ja-JP");

    const keywordFields = [
      "id",

      "user_id",

      "user_name",

      "show_user_name",

      "memo",
    ];

    return candidates.filter((candidate) => {
      if (
        query &&
        !keywordFields.some((field) =>
          asText(candidate[field]).toLocaleLowerCase("ja-JP").includes(query),
        )
      )
        return false;

      return Object.keys(CANDIDATE_OPTIONS).every(
        (field) =>
          appliedFilters[field] === "" ||
          Number(candidate[field]) === Number(appliedFilters[field]),
      );
    });
  }, [candidates, appliedFilters]);

  // Sort after filtering. Missing values stay at the bottom in both directions.
  const sortedCandidates = useMemo(() => {
    if (!sortColumn) return filteredCandidates;
    const numericFields = new Set([
      "id",
      "registration_type",
      "display_flag",
      "data_type",
    ]);
    const dateFields = new Set(["created_at", "updated_at"]);
    const compare = (a, b) => {
      const aEmpty = a == null || String(a).trim() === "";
      const bEmpty = b == null || String(b).trim() === "";
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
      const direction = sortDirection === "asc" ? 1 : -1;
      if (numericFields.has(sortColumn)) {
        const an = Number(a);
        const bn = Number(b);
        if (Number.isFinite(an) && Number.isFinite(bn))
          return (an - bn) * direction;
        if (!Number.isFinite(an) || !Number.isFinite(bn))
          return !Number.isFinite(an) === !Number.isFinite(bn)
            ? 0
            : !Number.isFinite(an)
              ? 1
              : -1;
      }
      if (dateFields.has(sortColumn)) {
        const parse = (value) =>
          /^\d+$/.test(String(value).trim())
            ? new Date(Number(value)).getTime()
            : new Date(String(value).replace(" ", "T")).getTime();
        const at = parse(a);
        const bt = parse(b);
        if (Number.isFinite(at) && Number.isFinite(bt))
          return (at - bt) * direction;
        if (!Number.isFinite(at) || !Number.isFinite(bt))
          return !Number.isFinite(at) === !Number.isFinite(bt)
            ? 0
            : !Number.isFinite(at)
              ? 1
              : -1;
      }
      return (
        String(a).localeCompare(String(b), "ja", {
          numeric: true,
          sensitivity: "base",
        }) * direction
      );
    };
    return filteredCandidates
      .map((candidate, index) => ({ candidate, index }))
      .sort(
        (a, b) =>
          compare(a.candidate[sortColumn], b.candidate[sortColumn]) ||
          a.index - b.index,
      )
      .map(({ candidate }) => candidate);
  }, [filteredCandidates, sortColumn, sortDirection]);

  const SortHeader = ({ field, children }) => (
    <th
      onClick={() => handleSort(field)}
      aria-sort={
        sortColumn === field
          ? sortDirection === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
      title="クリックして昇順・降順を切り替え"
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
    >
      {children}{" "}
      <span
        style={{
          color: sortColumn === field ? "#2563eb" : "#94a3b8",
          fontSize: 11,
        }}
      >
        {sortColumn === field ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </th>
  );

  useEffect(() => {
    if (!sessionStorage.getItem("userId")) {
      navigate("/");

      return;
    }

    loadCandidates();
  }, [loadCandidates, navigate]);

  const handleSearch = (event) => {
    event.preventDefault();

    setAppliedFilters({ keyword: keyword.trim(), ...draftFilters });
  };

  const handleReset = () => {
    setKeyword("");

    const empty = { registration_type: "", display_flag: "", data_type: "" };

    setDraftFilters(empty);

    setAppliedFilters({ keyword: "", ...empty });
  };

  const handleExportExcel = () => {
    if (
      isLoading ||
      exporting ||
      errorMessage ||
      filteredCandidates.length === 0
    )
      return;

    setExporting(true);

    try {
      // Use the same XLSX + file-saver implementation as ApprovaledlList.

      const worksheetData = sortedCandidates.map((candidate) => ({
        ID: asText(candidate.id),

        ユーザーID: asText(candidate.user_id),

        ユーザー名: asText(candidate.user_name),

        "表示用ユーザー名(X名)": asText(candidate.show_user_name),

        背景色: asText(candidate.hex_code),

        字体色: asText(candidate.hex_font_code),

        メモ: asText(candidate.memo),

        登録区分: optionLabel("registration_type", candidate.registration_type),

        表示フラグ: optionLabel("display_flag", candidate.display_flag),

        データ区分: optionLabel("data_type", candidate.data_type),

        作成日時: formatDateTime(candidate.created_at).replace(/^-$/, ""),

        更新日時: formatDateTime(candidate.updated_at).replace(/^-$/, ""),
      }));

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);

      worksheet["!cols"] = [
        { wch: 12 },

        { wch: 22 },

        { wch: 20 },

        { wch: 26 },

        { wch: 16 },

        { wch: 16 },

        { wch: 30 },

        { wch: 14 },

        { wch: 14 },

        { wch: 36 },

        { wch: 22 },

        { wch: 22 },
      ];

      // Explicitly keep ID values as text, including leading zeros.

      for (let row = 2; row <= worksheetData.length + 1; row += 1) {
        for (const column of ["A", "B"]) {
          const cell = worksheet[`${column}${row}`];

          if (cell) {
            cell.t = "s";

            cell.z = "@";
          }
        }
      }

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "候補者一覧");

      const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const now = new Date();

      const date = [now.getFullYear(), now.getMonth() + 1, now.getDate()]

        .map((value, index) =>
          index === 0 ? String(value) : String(value).padStart(2, "0"),
        )

        .join("");

      const time = [now.getHours(), now.getMinutes(), now.getSeconds()]

        .map((value) => String(value).padStart(2, "0"))

        .join("");

      saveAs(blob, `Proおすすめ候補者一覧_${date}_${time}.xlsx`);
    } catch (error) {
      console.error("Failed to export recommendation candidates:", error);

      toast.error("Excel出力に失敗しました");
    } finally {
      setExporting(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const openNew = () => {
    closeUserPicker();

    setEditingId("");

    setForm({ ...EMPTY_FORM });

    setModalOpen(true);
  };

  const openEdit = (candidate) => {
    closeUserPicker();

    setEditingId(asText(candidate.id));

    setForm({
      id: asText(candidate.id),

      user_id: asText(candidate.user_id),

      user_name: asText(candidate.user_name),

      show_user_name: asText(candidate.show_user_name),

      hex_code: asText(candidate.hex_code),

      hex_font_code: asText(candidate.hex_font_code),

      memo: asText(candidate.memo),

      registration_type: candidate.registration_type ?? 0,

      display_flag: candidate.display_flag ?? 1,

      data_type: candidate.data_type ?? 0,
    });

    setModalOpen(true);
  };

  const handleSave = async () => {
    const userId = form.user_id.trim();

    if (!userId) {
      toast.warning("ユーザーIDを入力してください");

      return;
    }

    if (userId.length > 100) {
      toast.warning("ユーザーIDは100文字以内で入力してください");

      return;
    }

    if (form.user_name.length > 100) {
      toast.warning("ユーザー名は100文字以内で入力してください");

      return;
    }

    if (form.show_user_name.length > 100) {
      toast.warning("表示用ユーザー名(X名)は100文字以内で入力してください");

      return;
    }

    if (form.hex_code.length > 20 || form.hex_font_code.length > 20) {
      toast.warning("HEXコードは20文字以内で入力してください");

      return;
    }

    if (!isValidHex(form.hex_code) || !isValidHex(form.hex_font_code)) {
      toast.warning("HEXコードは3～8桁の色コードで入力してください");

      return;
    }

    if (form.memo.length > 100) {
      toast.warning("メモは100文字以内で入力してください");

      return;
    }

    if (
      Object.keys(CANDIDATE_OPTIONS).some(
        (field) =>
          !Number.isInteger(Number(form[field])) ||
          !CANDIDATE_OPTIONS[field][Number(form[field])],
      )
    ) {
      toast.warning("登録区分・表示フラグ・データ区分を選択してください");

      return;
    }

    setSaving(true);

    try {
      await saveProRecommendationCandidate({
        id: editingId,

        user_id: userId,

        user_name: form.user_name.trim(),

        show_user_name: form.show_user_name.trim(),

        hex_code: form.hex_code.trim() || EMPTY_FORM.hex_code,

        hex_font_code: form.hex_font_code.trim() || EMPTY_FORM.hex_font_code,

        memo: form.memo.trim(),

        registration_type: Number(form.registration_type),

        display_flag: Number(form.display_flag),

        data_type: Number(form.data_type),
      });

      toast.success(
        editingId ? "候補者情報を更新しました" : "候補者を登録しました",
      );

      setModalOpen(false);

      await loadCandidates();
    } catch (error) {
      console.error("Failed to save recommendation candidate:", error);

      toast.error("保存に失敗しました: " + (error.message || "サーバーエラー"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (candidate) => {
    const name =
      candidate.show_user_name || candidate.user_name || candidate.user_id;

    if (!window.confirm(`候補者「${name}」を削除しますか？`)) {
      return;
    }

    setDeletingId(asText(candidate.id));

    try {
      await deleteProRecommendationCandidate(candidate.id);

      toast.success("候補者を削除しました");

      await loadCandidates();
    } catch (error) {
      console.error("Failed to delete recommendation candidate:", error);

      toast.error("削除に失敗しました: " + (error.message || "サーバーエラー"));
    } finally {
      setDeletingId("");
    }
  };

  return (
    <MainLayout>
      <RecommendationPageLayout>
        <RecommendationBody>
          <Title>Proおすすめ候補者管理</Title>

          <ToolbarRow>
            <SmallButton type="button" variant="success" onClick={openNew}>
              <FaPlus /> 新規登録
            </SmallButton>

            <SmallButton
              type="button"
              variant="dark"
              onClick={() => loadCandidates()}
              disabled={isLoading}
            >
              <FaSyncAlt /> {isLoading ? "読み込み中..." : "リフレッシュ"}
            </SmallButton>

            <SmallButton
              type="button"
              variant="success"
              onClick={handleExportExcel}
              disabled={
                isLoading ||
                exporting ||
                Boolean(errorMessage) ||
                filteredCandidates.length === 0
              }
            >
              <FaFileExcel /> {exporting ? "出力中..." : "Excelダウンロード"}
            </SmallButton>

            <Muted>
              候補者は、Proおすすめアプリの設定データとして使用されます。
            </Muted>
          </ToolbarRow>

          <Card>
            <CardHeader>
              <span>候補者一覧（{filteredCandidates.length}件）</span>
            </CardHeader>

            <CardBody>
              <form
                onSubmit={handleSearch}
                role="search"
                style={{
                  display: "flex",

                  flexWrap: "wrap",

                  alignItems: "center",

                  gap: "8px",

                  width: "100%",

                  marginBottom: "12px",
                }}
              >
                <SearchInput
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="ID・ユーザーID・名前・メモ"
                  maxLength={100}
                  aria-label="候補者を検索"
                  style={{
                    flex: "1 1 180px",

                    minWidth: "150px",

                    maxWidth: "280px",

                    width: "auto",
                  }}
                />

                {Object.entries(CANDIDATE_OPTIONS).map(([field, options]) => (
                  <Select
                    key={field}
                    aria-label={CANDIDATE_LABELS[field]}
                    title={CANDIDATE_LABELS[field]}
                    value={draftFilters[field]}
                    style={{
                      flex: field === "data_type" ? "1 1 200px" : "0 1 145px",

                      minWidth: field === "data_type" ? "185px" : "125px",

                      maxWidth: field === "data_type" ? "260px" : "160px",

                      width: "auto",
                    }}
                    onChange={(event) =>
                      setDraftFilters((previous) => ({
                        ...previous,

                        [field]: event.target.value,
                      }))
                    }
                  >
                    <option value="">{CANDIDATE_LABELS[field]}：すべて</option>

                    {options.map((label, value) => (
                      <option key={value} value={String(value)}>
                        {label}
                      </option>
                    ))}
                  </Select>
                ))}

                <div
                  style={{ display: "inline-flex", gap: "8px", flexShrink: 0 }}
                >
                  <SmallButton type="submit">
                    <FaSearch /> 検索
                  </SmallButton>

                  <SmallButton
                    type="button"
                    variant="secondary"
                    onClick={handleReset}
                  >
                    クリア
                  </SmallButton>
                </div>
              </form>

              {errorMessage && (
                <ErrorText role="alert">{errorMessage}</ErrorText>
              )}

              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <SortHeader field="id">ID</SortHeader>

                      <SortHeader field="user_id">ユーザーID</SortHeader>

                      <SortHeader field="user_name">ユーザー名</SortHeader>

                      <SortHeader field="show_user_name">
                        表示用ユーザー名(X名)
                      </SortHeader>

                      <SortHeader field="hex_code">背景色</SortHeader>

                      <SortHeader field="hex_font_code">字体色</SortHeader>

                      <SortHeader field="memo">メモ</SortHeader>

                      <SortHeader field="registration_type">
                        登録区分
                      </SortHeader>

                      <SortHeader field="display_flag">表示フラグ</SortHeader>

                      <SortHeader field="data_type">データ区分</SortHeader>

                      <SortHeader field="created_at">作成日時</SortHeader>

                      <SortHeader field="updated_at">更新日時</SortHeader>

                      <th>操作</th>
                    </tr>
                  </thead>

                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan={13}>
                          <EmptyState>読み込み中...</EmptyState>
                        </td>
                      </tr>
                    ) : filteredCandidates.length === 0 ? (
                      <tr>
                        <td colSpan={13}>
                          <EmptyState>
                            該当する候補者がありません。検索条件をご確認ください。
                          </EmptyState>
                        </td>
                      </tr>
                    ) : (
                      sortedCandidates.map((candidate) => (
                        <tr key={candidate.id}>
                          <td>{displayValue(candidate.id)}</td>

                          <td>{displayValue(candidate.user_id)}</td>

                          <td>{displayValue(candidate.user_name)}</td>

                          <td>{displayValue(candidate.show_user_name)}</td>

                          <td>
                            <ColorCell>
                              <ColorSwatch
                                style={{
                                  backgroundColor: swatchColor(
                                    candidate.hex_code,
                                  ),
                                }}
                              />

                              <span>{displayValue(candidate.hex_code)}</span>
                            </ColorCell>
                          </td>

                          <td>
                            <ColorCell>
                              <ColorSwatch
                                style={{
                                  backgroundColor: swatchColor(
                                    candidate.hex_font_code,
                                  ),
                                }}
                              />

                              <span>
                                {displayValue(candidate.hex_font_code)}
                              </span>
                            </ColorCell>
                          </td>

                          <td title={asText(candidate.memo)}>
                            {displayValue(candidate.memo)}
                          </td>

                          {Object.keys(CANDIDATE_OPTIONS).map((field) => (
                            <td key={field}>
                              {optionLabel(field, candidate[field])}
                            </td>
                          ))}

                          <td>{formatDateTime(candidate.created_at)}</td>

                          <td>{formatDateTime(candidate.updated_at)}</td>

                          <ActionCell>
                            <ActionGroup>
                              <SmallButton
                                type="button"
                                onClick={() => openEdit(candidate)}
                                disabled={Boolean(deletingId)}
                                aria-label="候補者を編集"
                              >
                                <FaPen /> 編集
                              </SmallButton>

                              <SmallButton
                                type="button"
                                variant="danger"
                                onClick={() => handleDelete(candidate)}
                                disabled={Boolean(deletingId)}
                                aria-label="候補者を削除"
                              >
                                <FaTrash /> 削除
                              </SmallButton>
                            </ActionGroup>
                          </ActionCell>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </TableWrapper>
            </CardBody>
          </Card>
        </RecommendationBody>
      </RecommendationPageLayout>

      {modalOpen && (
        <ModalOverlay>
          <ModalContent onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <span>{editingId ? "候補者 編集" : "候補者 新規登録"}</span>

              <SmallButton
                type="button"
                variant="secondary"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                × 閉じる
              </SmallButton>
            </ModalHeader>

            <FormGrid>
              <FullWidthField>
                <ToolbarRow>
                  <SmallButton
                    type="button"
                    onClick={openUserPicker}
                    disabled={saving}
                  >
                    <FaSearch /> ユーザー検索・選択
                  </SmallButton>

                  <Hint>
                    選択するとユーザーID・ユーザー名・表示用ユーザー名(X名)を自動設定します。
                  </Hint>
                </ToolbarRow>
              </FullWidthField>

              <Field>
                <Label htmlFor="recommendation-id">ID</Label>

                <Input
                  id="recommendation-id"
                  value={form.id}
                  readOnly
                  placeholder="自動採番"
                />

                <Hint>登録時に自動採番されます。</Hint>
              </Field>

              <Field>
                <Label htmlFor="recommendation-user-id">
                  ユーザーID
                  <Required>必須</Required>
                </Label>

                <Input
                  id="recommendation-user-id"
                  value={form.user_id}
                  onChange={(event) =>
                    handleChange("user_id", event.target.value)
                  }
                  maxLength={100}
                  placeholder="例：pro_user_001"
                />
              </Field>

              <Field>
                <Label htmlFor="recommendation-user-name">ユーザー名</Label>

                <Input
                  id="recommendation-user-name"
                  value={form.user_name}
                  onChange={(event) =>
                    handleChange("user_name", event.target.value)
                  }
                  maxLength={100}
                  placeholder="例：山田 太郎"
                />
              </Field>

              <Field>
                <Label htmlFor="recommendation-show-user-name">
                  表示用ユーザー名(X名)
                </Label>

                <Input
                  id="recommendation-show-user-name"
                  value={form.show_user_name}
                  onChange={(event) =>
                    handleChange("show_user_name", event.target.value)
                  }
                  maxLength={100}
                  placeholder="例：山田プロ"
                />
              </Field>

              <Field>
                <Label htmlFor="recommendation-background-color">
                  背景色HEXコード
                </Label>

                <ColorInputRow>
                  <ColorPicker
                    type="color"
                    value={toColorInputValue(form.hex_code)}
                    onChange={(event) =>
                      handleChange("hex_code", event.target.value.toUpperCase())
                    }
                    aria-label="背景色を選択"
                  />

                  <Input
                    id="recommendation-background-color"
                    value={form.hex_code}
                    onChange={(event) =>
                      handleChange("hex_code", event.target.value)
                    }
                    maxLength={20}
                    placeholder="#RRGGBB"
                  />
                </ColorInputRow>
              </Field>

              <Field>
                <Label htmlFor="recommendation-font-color">
                  字体色HEXコード
                </Label>

                <ColorInputRow>
                  <ColorPicker
                    type="color"
                    value={toColorInputValue(form.hex_font_code)}
                    onChange={(event) =>
                      handleChange(
                        "hex_font_code",

                        event.target.value.toUpperCase(),
                      )
                    }
                    aria-label="字体色を選択"
                  />

                  <Input
                    id="recommendation-font-color"
                    value={form.hex_font_code}
                    onChange={(event) =>
                      handleChange("hex_font_code", event.target.value)
                    }
                    maxLength={20}
                    placeholder="#RRGGBB"
                  />
                </ColorInputRow>
              </Field>

              {Object.entries(CANDIDATE_OPTIONS).map(([field, options]) => (
                <Field key={field}>
                  <Label htmlFor={`recommendation-${field}`}>
                    {CANDIDATE_LABELS[field]}
                  </Label>

                  <Select
                    id={`recommendation-${field}`}
                    value={form[field]}
                    onChange={(event) =>
                      handleChange(field, Number(event.target.value))
                    }
                  >
                    {options.map((label, value) => (
                      <option key={value} value={value}>
                        {value}：{label}
                      </option>
                    ))}
                  </Select>
                </Field>
              ))}

              <FullWidthField>
                <Label htmlFor="recommendation-memo">メモ</Label>

                <TextArea
                  id="recommendation-memo"
                  value={form.memo}
                  onChange={(event) => handleChange("memo", event.target.value)}
                  maxLength={100}
                  placeholder="おすすめ理由などを入力（100文字以内）"
                  rows={3}
                />
              </FullWidthField>
            </FormGrid>

            <ModalButtons>
              <SmallButton
                type="button"
                variant="secondary"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                キャンセル
              </SmallButton>

              <SmallButton
                type="button"
                variant="success"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "保存中..." : "保存"}
              </SmallButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {modalOpen && userPickerOpen && (
        <ModalOverlay>
          <ModalContent
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-picker-title"
          >
            <ModalHeader>
              <span id="user-picker-title">ユーザー検索・選択</span>

              <SmallButton
                type="button"
                variant="secondary"
                onClick={closeUserPicker}
              >
                × 閉じる
              </SmallButton>
            </ModalHeader>

            <SearchRow
              onSubmit={(event) => {
                event.preventDefault();

                loadUsers(userKeyword.trim());
              }}
            >
              <SearchInput
                autoFocus
                aria-label="ユーザーID・ユーザー名で検索"
                placeholder="ユーザーID・ユーザー名で検索"
                value={userKeyword}
                maxLength={100}
                onChange={(event) => setUserKeyword(event.target.value)}
              />

              <SmallButton type="submit" disabled={userLoading}>
                <FaSearch /> 検索
              </SmallButton>

              <SmallButton
                type="button"
                variant="secondary"
                disabled={userLoading}
                onClick={() => {
                  setUserKeyword("");

                  loadUsers();
                }}
              >
                リセット
              </SmallButton>
            </SearchRow>

            <Hint>選択後に変更できます。</Hint>

            {userError && <ErrorText role="alert">{userError}</ErrorText>}

            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>ユーザーID</th>

                    <th>ユーザー名</th>

                    <th>表示用ユーザー名(X名)</th>

                    <th>操作</th>
                  </tr>
                </thead>

                <tbody>
                  {userLoading ? (
                    <tr>
                      <td colSpan={4}>
                        <EmptyState>読み込み中...</EmptyState>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <EmptyState>
                          {userError
                            ? "検索に失敗しました。再度検索してください。"
                            : "該当するユーザーが見つかりません。"}
                        </EmptyState>
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.user_id}>
                        <td>{displayValue(user.user_id)}</td>

                        <td>{displayValue(user.user_name)}</td>

                        <td>{displayValue(user.show_user_name)}</td>

                        <ActionCell>
                          <SmallButton
                            type="button"
                            onClick={() => selectUser(user)}
                            aria-label={`${asText(user.user_name)} (${asText(user.user_id)}) を選択`}
                          >
                            選択
                          </SmallButton>
                        </ActionCell>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </TableWrapper>

            <ModalButtons>
              <SmallButton
                type="button"
                variant="secondary"
                disabled={userLoading || userPage <= 1}
                onClick={() => loadUsers(userQuery, userPage - 1)}
              >
                前へ
              </SmallButton>

              <Muted>{userPage}ページ（1ページ50件）</Muted>

              <SmallButton
                type="button"
                disabled={userLoading || !userHasMore}
                onClick={() => loadUsers(userQuery, userPage + 1)}
              >
                次へ
              </SmallButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </MainLayout>
  );
}
